import {OtsDateTime} from "../../components/OtsDateTime";
import {ObjectChangeLog} from "@ots-frontend/ots-data";
import {ReactNode} from "react";
import {
  OtsDataTable,
  OtsDataTableBody,
  OtsDataTableCell,
  OtsDataTableHead,
  OtsDataTableHeaderCell,
  OtsDataTableRow,
  OtsDataTableRowHeader,
} from "../../components/OtsDataTable";

export type EntityChangeLogProps = {
  logs?: ObjectChangeLog[];
  loading?: boolean;
  density?: "compact" | "default";
  ariaLabel?: string;
  emptyState?: ReactNode;
  formatFieldName?: (fieldName: string) => ReactNode;
  formatValue?: (value: string | undefined, fieldName: string) => ReactNode;
};


const defaultValue = (value?: string) => value || "—";

/** Entity-neutral audit table that flattens object-change records into field-level changes. */
export const EntityChangeLog = ({
  logs = [],
  loading = false,
  density = "default",
  ariaLabel = "Entity change log",
  emptyState = "No changes have been recorded.",
  formatFieldName = fieldName => fieldName,
  formatValue = defaultValue,
}: EntityChangeLogProps) => {
  const entries = logs.flatMap(log => (log.objectFieldChangeList ?? []).map(change => ({
    id: change.id,
    fieldName: change.fieldName,
    fromValue: change.fromValue,
    toValue: change.toValue,
    person: log.createdByPersonName,
    createDttm: log.createDttm,
  })));

  if (loading) return <p role="status" className="text-sm text-[var(--ots-text-muted)]">Loading change log…</p>;
  if (!entries.length) return <p className="text-sm text-[var(--ots-text-muted)]">{emptyState}</p>;

  return <OtsDataTable density={density} contained={false} aria-label={ariaLabel}>
    <OtsDataTableHead><OtsDataTableRow>
      <OtsDataTableHeaderCell>Field</OtsDataTableHeaderCell>
      <OtsDataTableHeaderCell>Previous</OtsDataTableHeaderCell>
      <OtsDataTableHeaderCell>New</OtsDataTableHeaderCell>
      <OtsDataTableHeaderCell>Changed by</OtsDataTableHeaderCell>
      <OtsDataTableHeaderCell>Changed</OtsDataTableHeaderCell>
    </OtsDataTableRow></OtsDataTableHead>
    <OtsDataTableBody>{entries.map(entry => <OtsDataTableRow key={entry.id}>
      <OtsDataTableRowHeader>{formatFieldName(entry.fieldName)}</OtsDataTableRowHeader>
      <OtsDataTableCell>{formatValue(entry.fromValue, entry.fieldName)}</OtsDataTableCell>
      <OtsDataTableCell>{formatValue(entry.toValue, entry.fieldName)}</OtsDataTableCell>
      <OtsDataTableCell>{entry.person || "System"}</OtsDataTableCell>
      <OtsDataTableCell><OtsDateTime value={entry.createDttm} /></OtsDataTableCell>
    </OtsDataTableRow>)}</OtsDataTableBody>
  </OtsDataTable>;
};
